import * as React from "react"

import { cn } from "@/shared/lib/cn"

/**
 * The panel's table.
 *
 * shadcn's primitives, restyled 22 August 2026 to TailAdmin's table density (MIT —
 * `shared/tailadmin/LICENSE.md`).
 *
 * ⚠️ **Cells were `p-2` and headers `h-10 px-2`.** That is shadcn's default, tuned for a compact
 * settings table, and §23.10 puts 3,000+ volunteers through these rows. At 8px a row of five
 * columns is a wall — the eye has no gap to use as a column boundary and reads the line as one
 * string. The template's `px-5 py-4` is the density this screen is actually read at.
 *
 * ⚠️ **The header row is a raised band, not just a rule.** A hairline is invisible the moment the
 * table scrolls past a card edge, and an operator who has lost the header is reading five unlabelled
 * columns of Arabic names and phone numbers.
 *
 * ⚠️ `text-start`, never `text-left`: §23.10 flips the panel at runtime (§5.6).
 */
function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    // ⚠️ Scrolls inside itself. A wide table that widened the page instead would move the
    // navigation rail and every other card sideways with it.
    <div
      data-slot="table-container"
      className="custom-scrollbar relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-theme-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("border-y border-gray-100 bg-gray-50", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("divide-y divide-gray-100 [&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-t border-gray-100 bg-gray-50 font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "transition-colors hover:bg-gray-50 has-aria-expanded:bg-gray-50 data-[state=selected]:bg-gray-100",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "px-5 py-3 text-start align-middle text-theme-xs font-medium whitespace-nowrap text-gray-500 [&:has([role=checkbox])]:pe-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCell({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "px-5 py-4 align-middle text-gray-700 [&:has([role=checkbox])]:pe-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("mt-4 text-theme-sm text-gray-500", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
